import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaBrain, FaHeartbeat, FaMicrophone, FaChartLine,
  FaNewspaper, FaCloud, FaFileContract, FaTimes,
  FaCheck
} from 'react-icons/fa';
import './Projects.css';

const projects = [
  {
    id: 'legal-rag',
    code: '01',
    title: 'Legal RAG Chatbot for Companies Act 2063 (Nepal)',
    category: 'RAG Systems',
    tags: ['RAG Systems', 'AI Agents'],
    icon: <FaFileContract />,
    highlight: '91.93% Accuracy (Ragas)',
    metric: 'Mean BERTScore: 0.8762 | Context Recall: 97.5%',
    shortDesc:
      'Hybrid & Graph RAG chatbot on Nepal\'s Companies Act 2063 using structure-aware chunking, Pinecone, and graph search in PostgreSQL to resolve section cross-references with RRF orchestration in LangGraph.',
    description:
      'Built a legal RAG chatbot on the Companies Act 2063 of Nepal using structure-aware chunking, Pinecone as the vector database, hybrid search, and graph search in PostgreSQL to resolve cross-referencing between sections, combined with Reciprocal Rank Fusion (RRF) and a re-ranker, orchestrated with LangGraph. Fine-tuned Qwen3-8B, achieving a Mean BERTScore of 0.8762, 91.7% format adherence, and a 4% forgetting index. Evaluated the system with Ragas on 60 QA pairs, scoring 0.9398 faithfulness, 0.8451 answer relevancy, 0.9171 context precision, and 0.9750 context recall, for an overall accuracy of 91.93%. Built the chat interface in React with Clerk for user authentication. Deployed the frontend, backend, and database on Render, and hosted the fine-tuned model on Modal.',
    tech: ['LangGraph', 'Qwen3-8B', 'Pinecone', 'PostgreSQL Graph', 'RRF', 'Ragas', 'React', 'Clerk', 'Modal', 'Render'],
    details: [
      'Structure-aware legal document chunking with PostgreSQL graph search for section cross-references',
      'Fine-tuned Qwen3-8B with 91.7% format adherence and only 4% forgetting index',
      'Ragas evaluation: 0.9398 faithfulness, 0.8451 answer relevancy, 0.9750 context recall',
      'Full deployment on Render (Frontend/Backend/DB) and Modal (LLM hosting)',
    ],
  },
  {
    id: 'mlops-loan',
    code: '02',
    title: 'End-to-End MLOps Pipeline for Loan Prediction',
    category: 'MLOps & Data',
    tags: ['MLOps & Data', 'Deep Learning'],
    icon: <FaChartLine />,
    highlight: 'Evidently AI Drift Monitoring',
    metric: 'Hot-Reloading & Automated Weekly Drift Reports',
    shortDesc:
      'Production-ready automated MLOps pipeline using Apache Airflow and MLflow for experiment tracking, hot-reloading FastAPI inference API, Redis prediction logging, and Evidently AI drift monitoring.',
    description:
      'Built a production-ready MLOps pipeline that automates the full machine learning lifecycle for loan approval prediction. Orchestrated data preprocessing, model training with GridSearchCV-tuned Logistic Regression, and experiment tracking using Apache Airflow and MLflow. Deployed a FastAPI inference API with automatic model hot-reloading from the MLflow Model Registry and real-time prediction logging to Redis. Integrated Evidently AI for automated weekly data drift and concept drift monitoring. Containerized the entire stack — Airflow, MLflow, FastAPI, MariaDB, Redis, and Jupyter Lab — using Docker Compose for reproducible, scalable deployment.',
    tech: ['Apache Airflow', 'MLflow', 'FastAPI', 'Docker Compose', 'Evidently AI', 'Redis', 'MariaDB', 'GridSearchCV'],
    details: [
      'Automated pipeline lifecycle: preprocessing, GridSearchCV tuning, and MLflow model registry',
      'FastAPI serving engine with automatic model hot-reloading without service downtime',
      'Real-time prediction logging to Redis with automated weekly data/concept drift reports via Evidently AI',
      'Fully reproducible multi-container stack orchestrated via Docker Compose',
    ],
  },
  {
    id: 'ai-image-detection',
    code: '03',
    title: 'AI-Generated Image Detection with Swin Transformer & Grad-CAM',
    category: 'Deep Learning',
    tags: ['Deep Learning'],
    icon: <FaBrain />,
    highlight: '98% Acc | 0.9972 ROC-AUC',
    metric: '35,000 Images | 8 Fake Generators Evaluated',
    shortDesc:
      'Deep learning detector fine-tuned on Tiny GenImage (35,000 images across 8 fakes like Midjourney & SD). Swin Transformer outperformed ViT, reaching 0.9790 F1 with Grad-CAM explainability.',
    description:
      'Built a deep learning model to detect real versus AI-generated images using the Tiny GenImage dataset (35,000 images), which includes fakes from eight generators: ADM, BigGAN, GLIDE, Midjourney, Stable Diffusion 1.4/1.5, VQDM, and Wukong. Fine-tuned and compared Swin Transformer and Vision Transformer (ViT) models with Hugging Face Transformers, selecting Swin for final training after it outperformed ViT. Preprocessed images with random resized cropping and normalization, and trained on 28,000 images with a 7,000-image validation set. Achieved 0.9775 precision, 0.9806 recall, 0.9790 F1-score, 0.9972 ROC-AUC, and 98% accuracy on balanced validation data. Implemented Grad-CAM to visualize the image regions driving each prediction and analyzed misclassified samples to understand the model\'s failure cases.',
    tech: ['Swin Transformer', 'Vision Transformer (ViT)', 'PyTorch', 'Hugging Face', 'Grad-CAM', 'Computer Vision'],
    details: [
      'Benchmarked against 8 generative models including Midjourney, Stable Diffusion 1.4/1.5, BigGAN, GLIDE',
      'Comparative study: Swin Transformer vs Vision Transformer (ViT)',
      'Metrics: 0.9775 Precision, 0.9806 Recall, 0.9790 F1-score, 0.9972 ROC-AUC',
      'Explainable AI via Grad-CAM heatmaps showing pixel regions driving predictions',
    ],
  },
  {
    id: 'health-agent',
    code: '04',
    title: 'Health Assistant AI Agent',
    category: 'AI Agents',
    tags: ['AI Agents'],
    icon: <FaHeartbeat />,
    highlight: 'MCP Protocol & LangSmith',
    metric: 'Stateful Graph Workflows & Tool Calling',
    shortDesc:
      'AI-powered health assistant for patient appointment scheduling, cancellations, and doctor lookups using stateful LangGraph, FastAPI, PostgreSQL, MCP servers, and LangSmith tracing.',
    description:
      'Developed an AI-powered health assistant for patient appointment management using LangGraph, FastAPI, PostgreSQL, and Docker. Designed a stateful LangGraph agent with tool calling for appointment booking, rescheduling, and cancellation workflows. Built a FastAPI backend with a PostgreSQL database storing patients, doctors, schedules, and appointments. Exposed backend operations as tools through Model Context Protocol (MCP) servers. Integrated LangSmith for tracing and observability of agent runs, tool calls, and LLM responses. Containerized the full stack with Docker.',
    tech: ['LangGraph', 'MCP Servers', 'FastAPI', 'PostgreSQL', 'LangSmith', 'Docker'],
    details: [
      'Stateful multi-step agent graphs managing doctor availability and appointment slots',
      'Standardized tool exposure via Model Context Protocol (MCP) servers',
      'Comprehensive tracing and observability with LangSmith across all agent decisions',
      'Containerized multi-service deployment with Docker',
    ],
  },
  {
    id: 'voice-agent',
    code: '05',
    title: 'AI Voice Agent',
    category: 'AI Agents',
    tags: ['AI Agents'],
    icon: <FaMicrophone />,
    highlight: 'LiveKit Multi-turn Voice',
    metric: 'Real-time STT/TTS & Silero VAD',
    shortDesc:
      'Real-time conversational voice agent leveraging OpenAI Speech-to-Text, ElevenLabs natural Text-to-Speech, Silero Voice Activity Detection, and GPT-4o conversational intelligence over LiveKit.',
    description:
      'Developed an AI-powered voice assistant capable of real-time spoken interactions. Implemented key components including speech-to-text (STT) with OpenAI, text-to-speech (TTS) using ElevenLabs, voice activity detection (VAD) with Silero, and conversational understanding with GPT-4o. Built the agent using LiveKit, enabling multi-turn voice sessions.',
    tech: ['LiveKit', 'OpenAI STT', 'ElevenLabs TTS', 'Silero VAD', 'GPT-4o', 'WebRTC'],
    details: [
      'Ultra-low-latency bidirectional audio streaming using LiveKit',
      'Natural interruption handling and turn-taking with Silero VAD',
      'Expressive vocal synthesis powered by ElevenLabs combined with GPT-4o reasoning',
    ],
  },
  {
    id: 'heart-risk',
    code: '06',
    title: 'Heart Risk Analysis Application',
    category: 'Deep Learning',
    tags: ['Deep Learning'],
    icon: <FaHeartbeat />,
    highlight: 'Deployed on Render',
    metric: 'Hyperparameter Tuned with GridSearchCV',
    shortDesc:
      'Interactive healthcare application predicting cardiovascular disease risk using deep learning, cross-validation, and GridSearchCV, with Streamlit frontend and FastAPI backend on Render.',
    description:
      'Developed a deep learning model to predict heart disease risk, with data preprocessing and feature engineering. Applied cross-validation and hyperparameter tuning with GridSearchCV. Built an interactive application with a Streamlit frontend and FastAPI backend, containerized it with Docker, and deployed it on Render.',
    tech: ['Deep Learning', 'FastAPI', 'Streamlit', 'Docker', 'Render', 'GridSearchCV'],
    details: [
      'Clinical feature engineering and exploratory data analysis on heart disease indicators',
      'Robust cross-validation and regularization to avoid overfitting',
      'Decoupled architecture: Streamlit client communicating with FastAPI inference backend',
    ],
  },
  {
    id: 'news-rag',
    code: '07',
    title: 'Chat With Latest News in SaaS Platform',
    category: 'RAG Systems',
    tags: ['RAG Systems'],
    icon: <FaNewspaper />,
    highlight: 'pgvector & Scheduled Crawl',
    metric: 'Automated Crawl & Real-time Vector Search',
    shortDesc:
      'FastAPI microservice for a SaaS platform that scheduled, crawled, and stored news in PostgreSQL with pgvector, enabling multi-turn RAG conversations on breaking world events.',
    description:
      'Developed a microservice for a SaaS platform using FastAPI to crawl, schedule, and store news articles in PostgreSQL with pgvector. Implemented an RAG-based chatbot that interacts with the latest news, enabling dynamic, multi-turn conversations grounded in retrieved context.',
    tech: ['FastAPI', 'PostgreSQL', 'pgvector', 'RAG', 'Web Scraping', 'Microservice'],
    details: [
      'Scheduled crawling pipeline extracting clean news text from external feeds',
      'Integrated dense semantic search utilizing pgvector inside PostgreSQL',
      'Multi-turn grounded context retrieval with hallucination guardrails',
    ],
  },
  {
    id: 'post-analysis',
    code: '08',
    title: 'Post Analysis Model',
    category: 'Deep Learning',
    tags: ['Deep Learning'],
    icon: <FaBrain />,
    highlight: 'NLP Sentiment Classification',
    metric: 'spaCy Custom Text Preprocessing Pipelines',
    shortDesc:
      'NLP sentiment classification pipeline analyzing social posts from X App via the X API. Engineered custom lemmatization and stemming workflows using spaCy and NLTK.',
    description:
      'Developed a post analysis model to classify posts in X App as positive or negative using spaCy. Used data from posts of X, fetched through X API, and performed feature engineering with spaCy and NLTK, including stemming and lemmatization. Optimized custom pipelines for efficient text preprocessing.',
    tech: ['spaCy', 'NLTK', 'X API', 'Python', 'NLP Pipelines', 'Sentiment Analysis'],
    details: [
      'Automated data ingestion from X (Twitter) API for real-time social sentiment data',
      'Custom text normalizers, tokenizers, lemmatizers, and stopword filtering',
      'Optimized pipeline execution for batch tweet classification',
    ],
  },
  {
    id: 'credit-score',
    code: '09',
    title: 'Credit Score Prediction Model',
    category: 'Deep Learning',
    tags: ['Deep Learning'],
    icon: <FaChartLine />,
    highlight: 'MLflow Tracking',
    metric: 'Deep Learning Regularization & Docker Deployment',
    shortDesc:
      'Deep learning classification model predicting individual creditworthiness. Engineered features, trained with regularization, tracked experiments in MLflow, and deployed via FastAPI + Streamlit.',
    description:
      'Developed a deep learning classification model to predict credit scores. Performed feature engineering, model training, hyperparameter tuning, and regularization, experimenting with different training methods. Built an application with Streamlit frontend and FastAPI backend, deployed using Docker, and utilized MLflow for experiment tracking and model performance management.',
    tech: ['Deep Learning', 'MLflow', 'FastAPI', 'Streamlit', 'Docker', 'Regularization'],
    details: [
      'Comprehensive financial feature scaling, outlier handling, and imputation',
      'Experiment comparison across architectures logged to MLflow UI',
      'Packaged and served with Docker containers for deterministic production runs',
    ],
  },
  {
    id: 'etl-weather',
    code: '10',
    title: 'ETL Pipeline for Weather Data',
    category: 'MLOps & Data',
    tags: ['MLOps & Data'],
    icon: <FaCloud />,
    highlight: 'Airflow & AWS S3',
    metric: 'Automated Scheduled Batch Extraction',
    shortDesc:
      'Automated ETL pipeline extracting weather APIs with Python, transforming via Pandas, and loading aggregated partitions into AWS S3 storage on Apache Airflow schedules.',
    description:
      'Developed an ETL pipeline to process weather data using Python for API extraction and Pandas for data manipulation. Used Airflow to schedule API extraction and loaded the cleaned and aggregated data into AWS S3 for scalable storage and analysis.',
    tech: ['Apache Airflow', 'AWS S3', 'Pandas', 'Python', 'API Extraction', 'ETL'],
    details: [
      'Airflow DAG orchestration with retry policies, failure alerting, and cron intervals',
      'Clean columnar transformation and data quality validations with Pandas',
      'Partitioned lakehouse ingestion into Amazon Web Services (AWS) S3',
    ],
  },
  {
    id: 'finance-dashboard',
    code: '11',
    title: 'People Finance Report Dashboard (USA)',
    category: 'MLOps & Data',
    tags: ['MLOps & Data'],
    icon: <FaChartLine />,
    highlight: 'Power BI & DAX',
    metric: 'Interactive Demographic Financial Insights',
    shortDesc:
      'Interactive Power BI financial intelligence dashboard analyzing income, expenditure, and savings patterns across US demographics with custom DAX calculations and drill-down filters.',
    description:
      'Created a Power BI dashboard to analyze the financial behavior of individuals in the USA, focusing on income, expenses, and savings patterns. Integrated interactive visualizations for real-time insights. Implemented filters and drill-down features for detailed insights, using DAX for data transformation and custom calculations.',
    tech: ['Power BI', 'DAX', 'Data Transformation', 'Interactive Visualizations', 'Financial Analytics'],
    details: [
      'Complex DAX measures for dynamic trend comparisons and cohort savings ratios',
      'Interactive slicing by age bracket, state, income group, and expense tier',
      'Executive-ready visual summaries designed for rapid financial trend discovery',
    ],
  },
];

const categories = ['All', 'AI Agents', 'RAG Systems', 'Deep Learning', 'MLOps & Data'];

const Projects = () => {
  const [active, setActive] = useState('All');
  const [selectedProject, setSelectedProject] = useState(null);

  const filtered =
    active === 'All'
      ? projects
      : projects.filter((p) => p.tags.includes(active));

  return (
    <section id="projects" className="projects">
      <div className="container">
        <div className="section-header">
          <span className="section-label">[ 03 // FEATURED WORKS ]</span>
          <h2 className="section-title">Selected Projects</h2>
          <p className="section-subtitle">
            A comprehensive catalog of 11 production-grade AI agents, legal RAG systems, deep learning models, and MLOps pipelines.
          </p>
        </div>

        <div className="projects__filters">
          {categories.map((cat) => (
            <button
              key={cat}
              className={`projects__filter ${active === cat ? 'projects__filter--active' : ''}`}
              onClick={() => setActive(cat)}
            >
              <span>{cat}</span>
              <span className="projects__filter-count">
                {cat === 'All'
                  ? projects.length
                  : projects.filter((p) => p.tags.includes(cat)).length}
              </span>
            </button>
          ))}
        </div>

        <div className="projects__grid">
          {filtered.map((project) => (
            <motion.div
              className="projects__card"
              key={project.id}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35 }}
              onClick={() => setSelectedProject(project)}
            >
              <div className="projects__card-top">
                <span className="projects__card-code">[{project.code}]</span>
                <span className="projects__card-category">{project.category}</span>
              </div>

              <h3 className="projects__card-title">{project.title}</h3>
              <p className="projects__card-desc">{project.shortDesc}</p>

              {project.highlight && (
                <div className="projects__card-highlight">
                  <span>{project.highlight}</span>
                </div>
              )}

              <div className="projects__card-tech">
                {project.tech.slice(0, 5).map((t) => (
                  <span key={t} className="tag">
                    {t}
                  </span>
                ))}
                {project.tech.length > 5 && (
                  <span className="tag projects__tech-more">
                    +{project.tech.length - 5}
                  </span>
                )}
              </div>

              <div className="projects__card-footer">
                <span className="projects__card-cta">
                  Explore Architecture &amp; Metrics &rarr;
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Modal for Deep Dive */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div
            className="projects__modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedProject(null)}
          >
            <motion.div
              className="projects__modal"
              initial={{ scale: 0.96, opacity: 0, y: 16 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.96, opacity: 0, y: 16 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="projects__modal-header">
                <div className="projects__modal-title-wrap">
                  <span className="projects__modal-category">
                    [{selectedProject.code} {'//'} {selectedProject.category}]
                  </span>
                  <h3 className="projects__modal-title">{selectedProject.title}</h3>
                </div>
                <button
                  className="projects__modal-close"
                  onClick={() => setSelectedProject(null)}
                  aria-label="Close details"
                >
                  <FaTimes />
                </button>
              </div>

              <div className="projects__modal-body">
                {selectedProject.highlight && (
                  <div className="projects__modal-highlight-box">
                    <span className="projects__modal-highlight-badge">
                      [ KEY RESULT ]
                    </span>
                    <strong>{selectedProject.highlight}</strong>
                    {selectedProject.metric && (
                      <span className="projects__modal-metric">
                        &bull; {selectedProject.metric}
                      </span>
                    )}
                  </div>
                )}

                <div className="projects__modal-section">
                  <h4>Full Technical Overview</h4>
                  <p className="projects__modal-desc">{selectedProject.description}</p>
                </div>

                <div className="projects__modal-section">
                  <h4>Key Architectural Highlights</h4>
                  <ul className="projects__modal-bullets">
                    {selectedProject.details.map((item, idx) => (
                      <li key={idx}>
                        <FaCheck className="projects__bullet-icon" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="projects__modal-section">
                  <h4>Technologies &amp; Tools</h4>
                  <div className="projects__modal-tech">
                    {selectedProject.tech.map((t) => (
                      <span key={t} className="tag">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="projects__modal-footer">
                <button
                  className="btn btn-primary"
                  onClick={() => setSelectedProject(null)}
                >
                  Close Specification
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Projects;
